import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getFaq } from "services/supportMaterialServices";
import useAppDispatch from "hooks/useAppDispatch";
import { setErrorStatus } from "features/error/errorSlice";
import type { FAQ } from "types/SupportMaterial";
import LoadingDialog from "components/__reusable/LoadingDialog";
import Category from "./Category";
import Subcategory from "./Subcategory";
import useHelpBackNavigation from "hooks/useHelpBackNavigation";
import { FAQ_PATH } from "router/pathName";
import type { HelpLocationState } from "hooks/useHelpBackNavigation";

const FAQPage = () => {
  useHelpBackNavigation();

  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { categoryId } = useParams();

  const selectedCategory = useMemo(() => {
    if (!categoryId) return undefined;
    const categoryNumber = Number(categoryId);
    return Number.isNaN(categoryNumber) ? undefined : categoryNumber;
  }, [categoryId]);

  const selectedFaqs = useMemo(
    () =>
      selectedCategory
        ? faqs.filter((faq) => faq.category_id === selectedCategory)
        : [],
    [faqs, selectedCategory]
  );

  const dispatch = useAppDispatch();
  const fetchFaq = async () => {
    setIsLoading(true);
    try {
      const faqs = await getFaq();
      setFaqs(faqs);
    } catch (e) {
      dispatch(setErrorStatus(""));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchFaq();
  }, []);

  const handleSelectCategory = (category: number) => {
    const state = location.state as HelpLocationState | null;
    navigate(`${FAQ_PATH}/${category}`, {
      state: {
        ...(state?.from ? { from: state.from } : {}),
        origin: FAQ_PATH,
      },
    });
  };

  return (
    <section className="h-full flex flex-col space-y-3 lg:space-y-5">
      {isLoading && <LoadingDialog />}
      {!selectedCategory ? (
        <Category faqs={faqs} onSelect={handleSelectCategory} />
      ) : (
        <Subcategory faqs={selectedFaqs} isLoading={isLoading} />
      )}
    </section>
  );
};

export default FAQPage;
